import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { cfpBaseUrl } from '../../network/urls';
import { resolveApiOfficeCode } from '../../utils/adminCompany';

const initialState = {
    data: [],
    currentPage: 1,
    pageSize: 20,
    dateFrom: '',
    dateTo: '',
    loadFrom: '',
    loadTo: '',
    applicationProgressId: '',
    objectionStatus: '',
    totalPages: null,
    totalRecords: null,
    isLoading: false,
    error: null,
};

export const fetchPaginatedWithdrawalOfInstallmentNormal = createAsyncThunk(
    'billingAndCollection/fetchWithdrawalOfInstallmentNormal',
    async (
        {
            page,
            pageSize,
            dateFrom,
            dateTo,
            applicationProgressId,
            loadFrom,
            loadTo,
            officeCode,
        },
        { getState },
    ) => {
        const { auth } = getState();
        const {
            token,
            code,
            hierarchy,
            roleId,
            userId,
            selectedAdminCompanyCode,
        } = auth;

        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);
        myHeaders.append('Content-Type', 'application/json');

        const effectiveOfficeCode = resolveApiOfficeCode(
            code,
            roleId,
            officeCode,
            selectedAdminCompanyCode,
        );

        const payload = {
            pageNumber: String(page || 1),
            pageSize: String(pageSize || 20),
            officeCode: String(effectiveOfficeCode ?? ''),
            officeLevel: String(code == 10 ? 2 : hierarchy ?? ''),
            overDueFlag: '0',
            dateFrom: dateFrom || '',
            dateTo: dateTo || '',
            loadFrom: loadFrom || '',
            loadTo: loadTo || '',
            applicationProgressId:
                applicationProgressId === 0 ||
                applicationProgressId === '0' ||
                (applicationProgressId !== undefined &&
                    applicationProgressId !== null &&
                    applicationProgressId !== '')
                    ? String(applicationProgressId)
                    : '',
            userId: roleId == 113 || roleId == 1146 ? String(userId ?? '') : '',
        };

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(payload),
            redirect: 'follow',
        };

        const response = await fetch(
            cfpBaseUrl + 'installments/withdrawal/getAllApplications',
            requestOptions,
        );
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Withdrawal applications API issue!');
        }

        const result = await response.json();
        const status = String(result?.status ?? '');

        if (status !== '200' && status !== '400' && status !== '204' && status !== '404') {
            throw new Error(result.message || 'Unable to fetch withdrawal applications!');
        }

        return result;
    },
);

const withdrawalOfInstallmentNormalPaginatedSlice = createSlice({
    name: 'withdrawalOfInstallmentNormalPaginated',
    initialState,
    reducers: {
        nextPage(state) {
            state.currentPage += 1;
        },
        previousPage(state) {
            if (state.currentPage > 1) {
                state.currentPage -= 1;
            }
        },
        filterProgressId(state, action) {
            state.applicationProgressId = action.payload;
        },
        resetProgressIdFilter(state) {
            state.applicationProgressId = '';
        },
        filterObjectionStatus(state, action) {
            state.objectionStatus = action.payload;
        },
        resetObjectionStatusFilter(state) {
            state.objectionStatus = '';
        },
        filterDateFrom(state, action) {
            state.dateFrom = action.payload;
        },
        filterDateTo(state, action) {
            state.dateTo = action.payload;
        },
        resetDateFilter(state) {
            state.dateFrom = '';
            state.dateTo = '';
        },
        firstPage(state) {
            state.currentPage = 1;
        },
        lastPage(state, action) {
            state.currentPage = action.payload;
        },
        resetState(state) {
            state.data = [];
            state.currentPage = 1;
            state.isLoading = false;
            state.pageSize = 20;
            state.error = null;
            state.dateFrom = '';
            state.dateTo = '';
            state.loadFrom = '';
            state.loadTo = '';
            state.applicationProgressId = '';
            state.objectionStatus = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPaginatedWithdrawalOfInstallmentNormal.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(fetchPaginatedWithdrawalOfInstallmentNormal.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
                const payload = action.payload || {};
                const rows = Array.isArray(payload.data) ? payload.data : [];
                state.data = rows;
                const totalRecords = Number(payload.totalRecords ?? payload.totalCount ?? rows.length);
                state.totalRecords = Number.isFinite(totalRecords) && totalRecords > 0 && rows.length > 0
                    ? totalRecords
                    : rows.length;
                const totalPages = Number(payload.totalPages);
                state.totalPages = Number.isFinite(totalPages) && totalPages > 0 && rows.length > 0
                    ? totalPages
                    : 1;
            })
            .addCase(fetchPaginatedWithdrawalOfInstallmentNormal.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message;
                state.data = [];
                state.totalPages = 1;
                state.totalRecords = 0;
            });
    },
});

export const {
    nextPage,
    previousPage,
    firstPage,
    lastPage,
    filterProgressId,
    filterObjectionStatus,
    filterDateFrom,
    filterDateTo,
    resetProgressIdFilter,
    resetObjectionStatusFilter,
    resetDateFilter,
    resetState,
} = withdrawalOfInstallmentNormalPaginatedSlice.actions;

export default withdrawalOfInstallmentNormalPaginatedSlice.reducer;
